import { CLOSE_CODES, PROTOCOL_VERSION, type EndMessage, type Input, type ServerMessage, type SnapshotMessage, type StartMessage } from '@chessshooter/shared';
import type { Connect, TokenStore, Transport } from './net';
import { RECONNECT_DELAYS_MS } from './netConstants';

// The connection state machine: hello/welcome, the queue, a running match and getting back into it
// after the socket dropped. It knows nothing about the simulation or the screen; the online mode
// (modes/online.ts) listens to its events. A token identifies the session on the server across
// sockets (protocol.ts): hello with the old token resumes a running match, and a welcome with a
// different token means the server has forgotten us, so the match is over.

export type SessionStatus =
  | { kind: 'connecting' }
  /** connected, not in a match, not waiting for one */
  | { kind: 'idle' }
  /** in the matchmaking queue; position unknown until the server answers */
  | { kind: 'queued'; position: number | null }
  /** reconnected during a match, the resumed start is on its way */
  | { kind: 'resuming' }
  | { kind: 'playing' }
  /** the socket is gone; the next attempt is scheduled */
  | { kind: 'offline'; retryInMs: number; attempt: number }
  /** another socket took this session over (another tab); no automatic reconnect */
  | { kind: 'replaced' }
  /** the server speaks another protocol version; a reload is the only way out */
  | { kind: 'outdated' };

export interface SessionEvents {
  onStatus(status: SessionStatus): void;
  onStart(msg: StartMessage): void;
  onSnapshot(msg: SnapshotMessage): void;
  onEnd(msg: EndMessage): void;
  /** the connection was away too long: the server has forgotten the match (lost by forfeit) */
  onMatchLost(): void;
  onPong(rttMs: number): void;
}

export interface SessionOptions {
  url: string;
  connect: Connect;
  tokens: TokenStore;
  events: SessionEvents;
  /** ms clock for the ping round trip; defaults to performance.now */
  now?: () => number;
  reconnectDelaysMs?: readonly number[];
}

export class Session {
  private transport: Transport | null = null;
  /** welcome received on the current socket */
  private connected = false;
  /** the user asked for a match and has not cancelled */
  private wantsMatch = false;
  /** the server holds a seat for us (start received, no end yet) */
  private inMatch = false;
  /** inputs may flow: in a match, on a welcomed socket, after the (resumed) start */
  private playing = false;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private current: SessionStatus = { kind: 'connecting' };

  constructor(private readonly opts: SessionOptions) {}

  status(): SessionStatus {
    return this.current;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  /** Opens a socket (unless one is open) and says hello with the stored token. */
  connect(): void {
    if (this.destroyed || this.transport) return;
    this.clearRetry();
    this.setStatus({ kind: 'connecting' });
    const transport = this.opts.connect(this.opts.url, {
      onOpen: () => {
        const token = this.opts.tokens.get();
        transport.send(token ? { type: 'hello', version: PROTOCOL_VERSION, token } : { type: 'hello', version: PROTOCOL_VERSION });
      },
      onMessage: (msg) => {
        if (transport === this.transport) this.handle(msg);
      },
      onClose: (code) => this.onClose(transport, code),
    });
    this.transport = transport;
  }

  /** Asks for a match; queues now or as soon as the connection is there. */
  play(): void {
    this.wantsMatch = true;
    if (this.inMatch) return;
    if (this.connected) this.sendQueue();
    else if (!this.transport && !this.retryTimer) this.connect();
  }

  /** Leaves the queue. Does nothing to a running match (see giveUp). */
  cancel(): void {
    this.wantsMatch = false;
    if (this.inMatch || !this.connected) return;
    this.transport?.send({ type: 'leave' });
    this.setStatus({ kind: 'idle' });
  }

  /** Forfeits the running match; the server answers with `end`. */
  giveUp(): void {
    if (this.inMatch && this.connected) this.transport?.send({ type: 'leave' });
  }

  sendInput(input: Input): void {
    if (this.playing) this.transport?.send({ type: 'input', input });
  }

  ping(): void {
    if (this.connected) this.transport?.send({ type: 'ping', t: this.now() });
  }

  /** The tab is visible again or the network is back: try at once instead of waiting out the backoff. */
  wake(): void {
    if (!this.transport && this.retryTimer) {
      this.clearRetry();
      this.connect();
    }
  }

  /** Dev aid: drops the socket so the reconnect path can be watched. */
  dropConnection(): void {
    this.transport?.close();
  }

  destroy(): void {
    this.destroyed = true;
    this.clearRetry();
    this.transport?.close();
    this.transport = null;
  }

  private now(): number {
    return this.opts.now ? this.opts.now() : performance.now();
  }

  private setStatus(status: SessionStatus): void {
    this.current = status;
    this.opts.events.onStatus(status);
  }

  private sendQueue(): void {
    this.transport?.send({ type: 'queue' });
    this.setStatus({ kind: 'queued', position: null });
  }

  private handle(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome': {
        const previous = this.opts.tokens.get();
        this.opts.tokens.set(msg.token);
        this.connected = true;
        this.attempt = 0;
        if (this.inMatch) {
          if (msg.token === previous) {
            this.setStatus({ kind: 'resuming' }); // the server sends the resumed start right after
          } else {
            this.inMatch = false;
            this.wantsMatch = false;
            this.setStatus({ kind: 'idle' });
            this.opts.events.onMatchLost();
          }
        } else if (this.wantsMatch) {
          this.sendQueue();
        } else {
          this.setStatus({ kind: 'idle' });
        }
        return;
      }
      case 'queued':
        this.setStatus({ kind: 'queued', position: msg.position });
        return;
      case 'start':
        this.inMatch = true;
        this.wantsMatch = false;
        this.playing = true;
        this.setStatus({ kind: 'playing' });
        this.opts.events.onStart(msg);
        return;
      case 'snapshot':
        if (this.inMatch) this.opts.events.onSnapshot(msg);
        return;
      case 'end':
        this.inMatch = false;
        this.playing = false;
        this.setStatus({ kind: 'idle' });
        this.opts.events.onEnd(msg);
        return;
      case 'pong':
        this.opts.events.onPong(Math.max(0, this.now() - msg.t));
        return;
      case 'error':
        // the version error is final (the close follows); every other one the server survives and so do we
        if (msg.code === 'version') this.setStatus({ kind: 'outdated' });
        return;
    }
  }

  private onClose(transport: Transport, code: number): void {
    if (transport !== this.transport) return;
    this.transport = null;
    this.connected = false;
    this.playing = false;
    if (this.destroyed) return;
    if (code === CLOSE_CODES.replaced) {
      this.setStatus({ kind: 'replaced' });
      return;
    }
    if (code === CLOSE_CODES.version || this.current.kind === 'outdated') {
      this.setStatus({ kind: 'outdated' });
      return;
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    const delays = this.opts.reconnectDelaysMs ?? RECONNECT_DELAYS_MS;
    const delay = delays[Math.min(this.attempt, delays.length - 1)];
    this.attempt++;
    this.setStatus({ kind: 'offline', retryInMs: delay, attempt: this.attempt });
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }
}
